import React, {useEffect, useMemo, useState} from 'react'
import Button from '../Button'
import Table from '../TableComponent'
import styles from './Categories.module.scss'
import {useAppContext, useFirebase} from '../Firebase/hooks'
import withAuthorization from '../Session/withAuthorization'
import Layout from '../Layout'
import NavBar from '../Navbar'
import {useNavigate, useLocation} from 'react-router-dom'
import {Loader} from '../Loader'
import {Category} from 'common'
import {TABLE_PAGINATION_PAGE_SIZE} from 'components/constants'
import Pagination from 'components/Pagination'
import {toast} from 'react-toastify'
import {useSortableData} from 'components/utils'

const Categories = () => {
  const {state} = useLocation()
  const [categories, setCategories] = useState<Array<Category>>([])
  const pageFromLocation = state?.page
  const [currentPage, setCurrentPage] = useState<number>(pageFromLocation || 1)
  const [loader, setLoader] = useState(true)
  const {getCategoriesList} = useFirebase()
  const navigate = useNavigate()
  const {
    appState: {isSuperManager}
  } = useAppContext()

  const cancelCallback = (error: Error) => {
    if (error) {
      toast.error(`Something went wrong while fetching categories.`)
    }
  }

  useEffect(() => {
    const unSubscribe = getCategoriesList<Category>({
      setCategories,
      cancelCallback
    })
    setTimeout(() => {
      setLoader(false)
    }, 2000)
    return () => {
      unSubscribe?.()
    }
  }, [])

  const getRowData = (rowData: Array<Category>) => {
    return (rowData || []).map((singleRowData, index: number) => {
      const {imageUrl, name, nodeId} = singleRowData
      return (
        <tr
          key={`category-row-data-${index}`}
          style={isSuperManager ? {cursor: 'unset'} : undefined}
          onClick={() => {
            if (isSuperManager) return
            navigate('/categories/single-category-details', {
              state: {
                id: nodeId,
                data: singleRowData,
                isEdit: true,
                currentPage
              },
              preventScrollReset: true
            })
          }}
        >
          <td>{nodeId}</td>
          <td>{name}</td>
          <td>
            <img
              className={styles.tableImage}
              src={imageUrl}
              alt="category-img"
            />
          </td>
        </tr>
      )
    })
  }

  const scrollRef: React.RefObject<HTMLDivElement> = React.createRef()

  const scrollToRef = () =>
    scrollRef?.current?.scrollIntoView({behavior: 'smooth'})

  const {items, sortBy} = useSortableData(categories)

  const currentTableData = useMemo(() => {
    const firstPageIndex = (currentPage - 1) * TABLE_PAGINATION_PAGE_SIZE
    const lastPageIndex = firstPageIndex + TABLE_PAGINATION_PAGE_SIZE
    return items.slice(firstPageIndex, lastPageIndex)
  }, [currentPage, items])

  const showPagination = categories?.length > TABLE_PAGINATION_PAGE_SIZE

  const onPageChange = (page: number) => {
    setCurrentPage(page)
    scrollToRef()
  }

  const tableTitles = [
    {title: 'Category Id', sortBy: () => sortBy('nodeId')},
    {title: 'Category', sortBy: () => sortBy('name')},
    {title: 'Image'}
  ]

  return (
    <div className={styles.withLayout}>
      <Layout />
      <div className={styles.container}>
        <NavBar title="Categories" />
        <div className={styles.button_filters}>
          {!isSuperManager && (
            <Button
              text="Add Category"
              customClass={styles.create_account_btn}
              onClick={() => navigate('/categories/single-category-details')}
            />
          )}
        </div>
        {loader ? (
          <Loader />
        ) : (
          <React.Fragment>
            <Table
              titles={tableTitles}
              customClass={styles.threedetails}
              rowData={getRowData(currentTableData)}
            />
            {showPagination && (
              <div className="pagination__wrapper">
                <Pagination
                  currentPage={currentPage}
                  onPageChange={onPageChange}
                  totalCount={categories?.length}
                  pageSize={TABLE_PAGINATION_PAGE_SIZE}
                />
              </div>
            )}
          </React.Fragment>
        )}
      </div>
    </div>
  )
}

export default withAuthorization(Categories)
